import { useState } from "react";
import { Button, ButtonVariant } from "shared/components";
import { HugeiconsIcon } from "@hugeicons/react";
import { ArrowRight02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { AlertCircleIcon } from "@hugeicons-pro/core-solid-rounded";
import { DocumentType } from "../types";
import { CombinedIncomeLayout } from "../components/Layout";
import { toast } from "react-toastify";
import { instance } from "api/apiBase";
import { useMutation } from "@tanstack/react-query";
import { IncomeType, VerifyMethodType } from "shared/types";
import { useFormTyped } from "shared/hooks";
import { useCombinedIncome } from "../context";
import SkipDocumentVerificationModal from "../components/SkipDocumentVerificationModal";
import { CombinedIncomeSubStep } from "../../../constants";
import { DropZone } from "shared/components/DropZone";
import { uploadFilesToS3 } from "../utils";
import { normalizeFilename, getFileUploadErrorMessage } from "shared/utils";
import { getErrorMessage } from "utils/error";

const VALID_FILE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/bmp",
  "image/tiff",
  "image/heic",
  "application/pdf"
];

type DocumentFormData = Record<string, string>;

const getDocumentConfig = (documentType: DocumentType) => {
  switch (documentType) {
    case DocumentType.OfferLetter:
      return {
        title: "Upload offer letter",
        description:
          "Upload applicant's offer letter to confirm upcoming employment."
      };
    case DocumentType.W2:
      return {
        title: "Upload W-2",
        description:
          "Upload applicant's income documents for the most recent 3 months. For faster and more accurate verification, upload the original PDFs."
      };
    case DocumentType.BankStatement:
      return {
        title: "Upload bank statements",
        description: (
          <div className="flex items-center gap-2.5">
            <HugeiconsIcon
              icon={AlertCircleIcon}
              size={20}
              className="text-warning-500 shrink-0"
            />
            <p>
              Verification via bank statements is currently not available. You
              can still upload documents for self-attested income documentation.
            </p>
          </div>
        )
      };
    case DocumentType.BenefitsStatement:
      return {
        title: "Upload benefits letter",
        description: (
          <div className="flex items-center gap-2.5">
            <HugeiconsIcon
              icon={AlertCircleIcon}
              size={20}
              className="text-warning-500 shrink-0"
            />
            <p>
              Verification via benefits letters is currently not available. You
              can still upload documents for self-attested income documentation.
            </p>
          </div>
        )
      };
    case DocumentType.Paystub:
    default:
      return {
        title: "Upload pay stubs",
        description:
          "Upload applicant's income documents for the most recent 3 months. For faster and more accurate verification, upload the original PDFs."
      };
  }
};

export const DocumentUploadStep = () => {
  const {
    application,
    applicant,
    verification,
    refetchVerification,
    stepData,
    updateStepMutation,
    getNextStep
  } = useCombinedIncome();

  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [
    skipDocumentVerificationModalOpen,
    setSkipDocumentVerificationModalOpen
  ] = useState(false);

  const effectiveDocumentType =
    stepData.currentIncomeType === IncomeType.OfferLetter
      ? DocumentType.OfferLetter
      : stepData.currentDocumentType || DocumentType.Paystub;

  const config = getDocumentConfig(effectiveDocumentType);

  const { onSubmit } = useFormTyped<DocumentFormData>({});

  const getDocumentTypeInfo = (docType?: DocumentType | null) => {
    switch (docType) {
      case DocumentType.OfferLetter:
        return {
          verifyMethodType: VerifyMethodType.OfferLetter,
          documentTypeString: "offer_letter"
        };
      case DocumentType.W2:
        return {
          verifyMethodType: VerifyMethodType.W2,
          documentTypeString: "w2"
        };
      case DocumentType.BankStatement:
        return {
          verifyMethodType: VerifyMethodType.BankStatement,
          documentTypeString: "bank_statement"
        };
      case DocumentType.BenefitsStatement:
        return {
          verifyMethodType: VerifyMethodType.BenefitsStatement,
          documentTypeString: "benefits_statement"
        };
      case DocumentType.Paystub:
      default:
        return {
          verifyMethodType: VerifyMethodType.Paystub,
          documentTypeString: "paystub"
        };
    }
  };

  const uploadDocumentsMutation = useMutation({
    mutationFn: async (formValues: DocumentFormData) => {
      const { verifyMethodType, documentTypeString } = getDocumentTypeInfo(
        effectiveDocumentType
      );

      const uploadedUrls = await uploadFilesToS3(uploadedFiles);

      const documents = uploadedFiles.map((file, idx) => ({
        source: formValues[`source_${idx}`] || undefined,
        document_type: documentTypeString,
        url: uploadedUrls[idx],
        metadata: {
          size: file.size,
          original_filename: normalizeFilename(file.name),
          content_type: file.type
        }
      }));

      const response = await instance.post(
        `/portal/v1/applications/${application.id}/verifications/${verification?.id}/income/${stepData.currentIncomeId}/create_income_source_with_documents?applicant_id=${applicant.id}`,
        {
          income_source_type: verifyMethodType,
          documents
        }
      );

      return response.data;
    },
    onSuccess: () => {
      const nextStep = getNextStep(CombinedIncomeSubStep.DocumentUpload);
      if (!nextStep) return;
      updateStepMutation.mutate(
        {
          lastStep: nextStep
        },
        {
          onSuccess: () => {
            refetchVerification();
          }
        }
      );
    },
    onError: (error) => {
      toast.error(
        getFileUploadErrorMessage(error) ||
          getErrorMessage(error) ||
          "Failed to upload documents"
      );
    }
  });

  return (
    <form
      onSubmit={onSubmit({
        onSuccess: (formValues) => {
          uploadDocumentsMutation.mutate(formValues);
        },
        onError: () => {
          toast.error("Please select an income source for all documents");
        }
      })}
    >
      <CombinedIncomeLayout
        title={config.title}
        subtitle={config.description}
        contentClassName="px-[70px]"
        footer={
          <>
            <Button
              className="w-[106px]"
              HugeIcon={ArrowRight02Icon}
              iconPosition="right"
              onClick={() => setSkipDocumentVerificationModalOpen(true)}
              disabled={!!uploadedFiles.length}
            >
              Skip
            </Button>
            <Button
              variant={ButtonVariant.PRIMARY}
              className="w-[132px]"
              type="submit"
              loading={
                uploadDocumentsMutation.isPending ||
                updateStepMutation.isPending
              }
              disabled={uploadedFiles.length === 0}
            >
              Continue
            </Button>
          </>
        }
      >
        <DropZone
          id="document-upload"
          title="Upload documents"
          subText="Maximum file size allowed: 25 MB"
          value={uploadedFiles}
          setValue={setUploadedFiles}
          accept={
            stepData.currentDocumentType === DocumentType.BankStatement
              ? "application/pdf"
              : VALID_FILE_TYPES.join(",")
          }
          multiple
          maxFileSize={{
            size: 25 * 1024 * 1024,
            message: "File size should be less than 25MB"
          }}
        />
        <SkipDocumentVerificationModal
          opened={skipDocumentVerificationModalOpen}
          onClose={() => setSkipDocumentVerificationModalOpen(false)}
        />
      </CombinedIncomeLayout>
    </form>
  );
};
